// AxisPatterns.js — Phase 2: how ONE axis plays out across the script, at every level.
// Three heatmap strips (Acts / Sequences / Scenes): each unit is a cell, in story order,
// coloured by its score on the chosen axis. A writer sees the axis's trajectory — where
// Development sags, where Pressure spikes — and how that differs act-to-act vs scene-to-scene.
// Data is already there: der_result carries every axis at every grain.

import React from 'react';
import PropTypes from 'prop-types';
import { grainTitle } from './grainNouns';
import { METRIC_LABELS } from './Constants';
import { unitDisplayLabel, unitSortKey } from './unitLabels';
import { tip } from './Tip';

const NAVY = '#14536e';
// Order is structure (coarse to fine); the nouns are per-format — grainNouns.js.
const GRAIN_KEYS = ['act', 'sequence', 'scene'];

// heatmap fill by score (muted so many cells read as a field, not alarm); not-owed = neutral.
function cellColor(sc, notOwed) {
  if (notOwed) return '#e9eef2';
  if (sc == null) return '#f1f5f9';
  if (sc < 3) return '#d9534f';
  if (sc < 5) return '#e8908f';
  if (sc <= 6) return '#e6c074';
  if (sc <= 8) return '#84c79b';
  return '#3f9d63';
}
const isNotOwed = (o, s) => s === 'na' || ['light', 'not_owed', 'optional'].includes(o);
function unitLabel(n) {
  return unitDisplayLabel(n, 'list') || n.label || 'Unit';
}

export default function AxisPatterns({ nodes, axis, onOpenUnit, script = null }) {
  const label = METRIC_LABELS[axis] || axis;
  return (
    <div>
      <div style={{ color: NAVY, fontWeight: 700, fontSize: '1.05rem', marginBottom: 4 }}>
        {label} across the script
      </div>
      <div style={{ color: '#64748b', fontSize: '.86rem', marginBottom: 16, lineHeight: 1.5 }}>
        One axis, every level — each cell is a unit in story order, coloured by its {label.toLowerCase()} score.
        Read each row left→right to see the trajectory; compare rows to see how it differs by level.
      </div>
      {GRAIN_KEYS.map((t) => {
        const grainLabel = grainTitle(script, t, t);
        const units = nodes.filter(n => n.nodeType === t && n.detailedContent).sort((a, b) => unitSortKey(a) - unitSortKey(b));
        if (!units.length) return null;
        const labelInline = t !== 'scene'; // few acts/seqs → label the cell; many scenes → colour only
        return (
          <div key={t} style={{ marginBottom: 20 }}>
            <div style={{ color: NAVY, fontWeight: 600, fontSize: '.9rem', marginBottom: 6 }}>{grainLabel}</div>
            <div className="d-flex" style={{ gap: 3, flexWrap: 'wrap' }}>
              {units.map(u => {
                const cell = u.detailedContent.scores?.[axis] || {};
                const sc = cell.score != null ? parseFloat(cell.score) : null;
                const notOwed = isNotOwed(cell.obligation, cell.status);
                const bg = cellColor(sc, notOwed);
                const dark = !notOwed && sc != null && (sc < 5 || sc > 8);
                return (
                  <button key={u.id} type="button" onClick={() => onOpenUnit(u)}
                    {...tip(`${unitLabel(u)} · ${label} ${notOwed ? 'not owed' : (sc != null ? sc.toFixed(1) : 'n/a')}`)}
                    style={{
                      flex: t === 'scene' ? '1 0 28px' : '0 0 auto', minWidth: t === 'scene' ? 28 : 70,
                      height: 34, border: '1px solid rgba(15,23,42,.08)', borderRadius: 6, background: bg,
                      color: dark ? '#fff' : '#0f172a', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer',
                      padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                    {labelInline ? unitDisplayLabel(u, 'compact') : (notOwed ? '' : (sc != null ? sc.toFixed(1) : ''))}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ color: '#94a3b8', fontSize: '.78rem', marginTop: 4 }}>
        red needs work · amber functional · green strong · gray = not owed at that unit · click any cell to open it
      </div>
    </div>
  );
}
AxisPatterns.propTypes = {
  nodes: PropTypes.array.isRequired,
  axis: PropTypes.string.isRequired,
  onOpenUnit: PropTypes.func.isRequired,
};
